'use client'

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { readStoredToken } from '../auth/auth-storage'
import { bindCard } from './api-client'
import { CheckoutDialog } from './checkout/checkout-dialog'
import { createBindCardStrategy } from './checkout/checkout-strategies'

interface BindCardCheckoutModalProps {
  open: boolean
  userEmail: string | null
  onClose: () => void
  onSuccess: () => void
  getTokenFn?: () => string | null
  bindCardFn?: typeof bindCard
  skipScript?: boolean
}

/**
 * Reusable no-charge card verification dialog. Pricing uses it while retaining
 * the pending Pro quote, so a missing card never turns into a navigation detour
 * or an implicit charge.
 */
export function BindCardCheckoutModal({
  open,
  userEmail,
  onClose,
  onSuccess,
  getTokenFn = readStoredToken,
  bindCardFn = bindCard,
  skipScript,
}: BindCardCheckoutModalProps) {
  const { t } = useTranslation('web')
  const strategy = useMemo(
    () => createBindCardStrategy({ bindCardFn, getTokenFn, t }),
    [bindCardFn, getTokenFn, t]
  )

  return (
    <CheckoutDialog
      visible={open}
      onClose={onClose}
      title={t('billing.bindCardModalTitle')}
      subtitle={t('billing.bindCardModalSubtitle')}
      dialogTestId="pricing-bind-card-modal"
      closeTestId="pricing-bind-card-close"
      amount={0}
      wallet={null}
      coupon={null}
      userEmail={userEmail}
      strategy={strategy}
      note={t('billing.payNote')}
      cardFree={false}
      submitLabelOverride={t('billing.bindCardSubmit')}
      testIdPrefix="pricing-bind"
      formTestId="pricing-bind-card-form"
      resetKey="pricing-upgrade-bind-card"
      onSuccess={onSuccess}
      skipScript={skipScript}
    />
  )
}
